import type { ContentBlock } from '../content/types'

function richText(text: string) {
  const parts = text.split(/(`[^`]+`|\*\*[^*]+\*\*)/g)
  return parts.map((part, i) => {
    if (part.startsWith('`') && part.endsWith('`')) {
      return (
        <code key={i} className="inline-code">
          {part.slice(1, -1)}
        </code>
      )
    }
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i}>{part.slice(2, -2)}</strong>
    }
    return <span key={i}>{part}</span>
  })
}

export function DocBlocks({ blocks }: { blocks: ContentBlock[] }) {
  return (
    <div className="doc-blocks">
      {blocks.map((block, index) => {
        switch (block.type) {
          case 'heading':
            if (block.level === 2) {
              return (
                <h2 key={index} id={block.id} className="doc-h2">
                  {block.text}
                </h2>
              )
            }
            return (
              <h3 key={index} id={block.id} className="doc-h3">
                {block.text}
              </h3>
            )
          case 'paragraph':
            return (
              <p key={index} className="doc-p">
                {richText(block.text)}
              </p>
            )
          case 'list':
            return block.ordered ? (
              <ol key={index} className="doc-list">
                {block.items.map((item, i) => (
                  <li key={i}>{richText(item)}</li>
                ))}
              </ol>
            ) : (
              <ul key={index} className="doc-list">
                {block.items.map((item, i) => (
                  <li key={i}>{richText(item)}</li>
                ))}
              </ul>
            )
          case 'steps':
            return (
              <ol key={index} className="doc-steps">
                {block.items.map((item, i) => (
                  <li key={i}>
                    <span className="step-index">{i + 1}</span>
                    <span>{richText(item)}</span>
                  </li>
                ))}
              </ol>
            )
          case 'table':
            return (
              <div key={index} className="table-wrap">
                <table className={`doc-table${block.monoFirst ? ' mono-first' : ''}`}>
                  <thead>
                    <tr>
                      {block.headers.map((h) => (
                        <th key={h}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {block.rows.map((row, ri) => (
                      <tr key={ri}>
                        {row.map((cell, ci) => (
                          <td key={ci}>{richText(cell)}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          case 'callout':
            return (
              <aside
                key={index}
                className={`callout callout-${block.variant}`}
                role="note"
              >
                {block.title ? <strong className="callout-title">{block.title}</strong> : null}
                <p>{richText(block.text)}</p>
              </aside>
            )
          case 'code':
            return (
              <figure key={index} className="code-figure">
                <pre>
                  <code>{block.code}</code>
                </pre>
                {block.caption ? <figcaption>{block.caption}</figcaption> : null}
              </figure>
            )
          case 'figure':
            return (
              <figure
                key={index}
                className={`doc-figure${block.size && block.size !== 'full' ? ` doc-figure-${block.size}` : ''}`}
              >
                <div className="doc-figure-frame">
                  <img src={block.src} alt={block.alt} loading="lazy" />
                </div>
                <figcaption>
                  <span className="fig-caption">{block.caption}</span>
                  {block.placeholderNote ? (
                    <span className="fig-note">{block.placeholderNote}</span>
                  ) : null}
                </figcaption>
              </figure>
            )
          case 'definitions':
            return (
              <dl key={index} className="doc-definitions">
                {block.items.map((item) => (
                  <div key={item.term} className="definition-row">
                    <dt>{item.term}</dt>
                    <dd>{richText(item.definition)}</dd>
                  </div>
                ))}
              </dl>
            )
          default:
            return null
        }
      })}
    </div>
  )
}
